import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  Eye,
  EyeOff,
  Filter,
  RefreshCw,
  Search,
  ShieldCheck,
  Workflow,
  X,
} from "lucide-react";

type StatusFilter = "ALL" | "NEW" | "REVIEWED" | "RESOLVED" | "IGNORED";
type TypeFilter = "ALL" | "FRONTEND" | "BACKEND" | "QUESTIONNAIRE";
type OriginFilter = "ALL" | "PLATAFORMA" | "NR1" | "QUESTIONNAIRE" | "BACKEND";

type StatusVariant = "default" | "secondary" | "destructive" | "outline";

type ErrorLogRecord = {
  id: number;
  errorType: "FRONTEND" | "BACKEND" | "QUESTIONNAIRE" | string;
  errorMessage: string;
  errorStack?: string | null;
  componentStack?: string | null;
  url?: string | null;
  userEmail?: string | null;
  companyId?: number | null;
  campaignId?: number | null;
  channelId?: number | null;
  context?: Record<string, unknown> | null;
  status: "NEW" | "REVIEWED" | "RESOLVED" | "IGNORED" | string;
  resolutionNote?: string | null;
  createdAt: number;
};

const STATUS_LABELS: Record<string, { label: string; variant: StatusVariant }> = {
  NEW: { label: "Novo", variant: "destructive" },
  REVIEWED: { label: "Em análise", variant: "default" },
  RESOLVED: { label: "Resolvido", variant: "secondary" },
  IGNORED: { label: "Ignorado", variant: "outline" },
};

const TYPE_LABELS: Record<string, string> = {
  FRONTEND: "Frontend",
  BACKEND: "Backend",
  QUESTIONNAIRE: "Questionário",
};

const ORIGIN_LABELS: Record<OriginFilter, string> = {
  ALL: "Todas as origens",
  PLATAFORMA: "Portal e administração",
  NR1: "NR1",
  QUESTIONNAIRE: "Questionário público",
  BACKEND: "Serviços internos",
};

function formatDate(ts: number) {
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function readString(value: unknown) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function getOriginFromLog(log: Partial<ErrorLogRecord>): Exclude<OriginFilter, "ALL"> {
  const url = readString(log.url);
  const context = (log.context && typeof log.context === "object" ? log.context : {}) as Record<string, unknown>;
  const explicitSource = readString(context.origin) || readString(context.source) || readString(context.module);

  if (log.errorType === "QUESTIONNAIRE" || explicitSource.includes("question") || url.includes("questionario-publico")) {
    return "QUESTIONNAIRE";
  }

  if (
    explicitSource.includes("nr1") ||
    url.includes("/nr1") ||
    url.includes("/importacao") ||
    url.includes("/questionarios") ||
    url.includes("/questionario") ||
    url.includes("/evaluations") ||
    url.includes("/documents") ||
    url.includes("/motor-analise") ||
    url.includes("/dashboards/") ||
    url.includes("/meus-resultados") ||
    url.includes("/minha-empresa") ||
    url.includes("/ergos")
  ) {
    return "NR1";
  }

  if (
    explicitSource.includes("portal") ||
    explicitSource.includes("nucleo") ||
    url.includes("/portal") ||
    url.includes("/modulos/nucleo") ||
    url.includes("/channels") ||
    url.includes("/clients") ||
    url.includes("/contratos") ||
    url.includes("/servicos") ||
    url.includes("/configuracoes") ||
    url.includes("/sistema/erros")
  ) {
    return "PLATAFORMA";
  }

  if (log.errorType === "BACKEND") {
    return "BACKEND";
  }

  return "PLATAFORMA";
}

function getOriginBadgeClasses(origin: Exclude<OriginFilter, "ALL">) {
  if (origin === "NR1") return "bg-emerald-50 text-emerald-700";
  if (origin === "QUESTIONNAIRE") return "bg-amber-50 text-amber-700";
  if (origin === "BACKEND") return "bg-rose-50 text-rose-700";
  return "bg-sky-50 text-sky-700";
}

function ErrorDetailModal({
  errorId,
  onClose,
}: {
  errorId: number | null;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const [showStack, setShowStack] = useState(false);
  const utils = trpc.useUtils();

  const { data: log } = trpc.systemErrors.getById.useQuery({ id: errorId! }, { enabled: !!errorId });

  const updateStatus = trpc.systemErrors.updateStatus.useMutation({
    onSuccess: () => {
      utils.systemErrors.list.invalidate();
      utils.systemErrors.summary.invalidate();
      onClose();
    },
  });

  if (!log) return null;

  const statusInfo = STATUS_LABELS[log.status] || STATUS_LABELS.NEW;
  const origin = getOriginFromLog(log);

  return (
    <Dialog open={!!errorId} onOpenChange={() => onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Bug className="h-5 w-5 text-rose-500" />
            Detalhe do log #{log.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Origem</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{ORIGIN_LABELS[origin]}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Tipo</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{TYPE_LABELS[log.errorType] || log.errorType}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Status</p>
              <div className="mt-2">
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Data e hora</p>
              <p className="mt-2 text-sm font-semibold text-slate-900">{formatDate(log.createdAt)}</p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {log.userEmail ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Usuário</p>
                <p className="mt-2 text-sm font-medium text-slate-900">{log.userEmail}</p>
              </div>
            ) : null}
            {log.companyId ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Empresa</p>
                <p className="mt-2 text-sm font-medium text-slate-900">#{log.companyId}</p>
              </div>
            ) : null}
            {log.campaignId ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Campanha</p>
                <p className="mt-2 text-sm font-medium text-slate-900">#{log.campaignId}</p>
              </div>
            ) : null}
          </div>

          {log.url ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">URL</p>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 break-all">
                {log.url}
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Mensagem</p>
            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 font-mono text-sm text-rose-800 break-words">
              {log.errorMessage}
            </div>
          </div>

          {log.errorStack ? (
            <div>
              <button
                className="mb-2 flex items-center gap-1 text-xs text-slate-500 transition-colors hover:text-slate-900"
                onClick={() => setShowStack(!showStack)}
              >
                {showStack ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {showStack ? "Ocultar" : "Ver"} stack trace
              </button>
              {showStack ? (
                <pre className="max-h-64 overflow-x-auto whitespace-pre-wrap rounded-2xl bg-slate-950 px-4 py-4 text-xs text-slate-100">
                  {log.errorStack}
                </pre>
              ) : null}
            </div>
          ) : null}

          {log.componentStack && showStack ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Component stack</p>
              <pre className="max-h-48 overflow-x-auto whitespace-pre-wrap rounded-2xl bg-slate-800 px-4 py-4 text-xs text-slate-100">
                {log.componentStack}
              </pre>
            </div>
          ) : null}

          {log.context ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Contexto adicional</p>
              <pre className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-xs text-slate-700">
                {JSON.stringify(log.context, null, 2)}
              </pre>
            </div>
          ) : null}

          {log.resolutionNote ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nota de resolução</p>
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                {log.resolutionNote}
              </div>
            </div>
          ) : null}

          {log.status !== "RESOLVED" && log.status !== "IGNORED" ? (
            <div className="border-t border-slate-200 pt-5">
              <Label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Nota de resolução</Label>
              <Textarea
                placeholder="Descreva a causa e a ação tomada..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="text-sm"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => updateStatus.mutate({ id: log.id, status: "REVIEWED", resolutionNote: note || undefined })}
                  disabled={updateStatus.isPending}
                >
                  Marcar em análise
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => updateStatus.mutate({ id: log.id, status: "RESOLVED", resolutionNote: note || undefined })}
                  disabled={updateStatus.isPending}
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  Resolver
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => updateStatus.mutate({ id: log.id, status: "IGNORED" })}
                  disabled={updateStatus.isPending}
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Ignorar
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SystemErrorLogs() {
  usePageTitle("Central de logs do sistema");

  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const [originFilter, setOriginFilter] = useState<OriginFilter>("ALL");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  if (user && user.role !== "admin") {
    navigate("/");
    return null;
  }

  const { data: summary } = trpc.systemErrors.summary.useQuery();
  const {
    data,
    isLoading,
    refetch,
  } = trpc.systemErrors.list.useQuery({
    page,
    pageSize: 50,
    status: statusFilter,
    errorType: typeFilter,
    origin: originFilter,
    search: search || undefined,
  });

  const items = useMemo(() => (data?.items ?? []) as ErrorLogRecord[], [data?.items]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / 50)) : 1;

  const visibleOrigins = useMemo(() => {
    return Array.from(new Set(items.map((item) => getOriginFromLog(item))));
  }, [items]);

  function handleSearch() {
    setSearch(searchInput.trim());
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setSearchInput("");
    setStatusFilter("ALL");
    setTypeFilter("ALL");
    setOriginFilter("ALL");
    setPage(1);
  }

  return (
    <SalviaLayout title="Central de logs do sistema">
      <div className="space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(190,242,100,0.18),_transparent_30%),linear-gradient(135deg,_#041833_0%,_#0b2746_55%,_#102f57_100%)] p-6 text-white shadow-[0_28px_90px_-40px_rgba(2,6,23,0.85)] lg:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl space-y-4">
              <Badge className="w-fit rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-lime-100 hover:bg-lime-300/10">
                Administração da Plataforma
              </Badge>
              <div className="space-y-3">
                <h1 className="text-3xl font-semibold tracking-tight text-white lg:text-4xl">
                  Central de logs do sistema
                </h1>
                <p className="max-w-3xl text-sm leading-7 text-slate-200/90 lg:text-base">
                  Esta tela passa a concentrar a leitura dos erros do sistema como um todo. O NR1 continua presente, mas agora aparece como origem dentro da central, sem manter uma área paralela no submenu Avançado.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Total de registros</p>
                <p className="mt-2 text-2xl font-semibold text-white">{summary?.total ?? 0}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Novos</p>
                <p className="mt-2 text-2xl font-semibold text-white">{summary?.NEW ?? 0}</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">Origens visíveis</p>
                <p className="mt-2 text-sm font-semibold text-white">
                  {visibleOrigins.length > 0 ? visibleOrigins.map((origin) => ORIGIN_LABELS[origin]).join(" · ") : "Sem registros nesta leitura"}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-4">
          {[
            { key: "NEW", label: "Novos", value: summary?.NEW ?? 0, tone: "text-rose-700 bg-rose-50", icon: AlertTriangle },
            { key: "REVIEWED", label: "Em análise", value: summary?.REVIEWED ?? 0, tone: "text-blue-700 bg-blue-50", icon: Workflow },
            { key: "RESOLVED", label: "Resolvidos", value: summary?.RESOLVED ?? 0, tone: "text-emerald-700 bg-emerald-50", icon: CheckCircle2 },
            { key: "IGNORED", label: "Ignorados", value: summary?.IGNORED ?? 0, tone: "text-slate-700 bg-slate-100", icon: ShieldCheck },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.key} className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                    <p className="mt-2 text-3xl font-semibold text-slate-950">{card.value}</p>
                  </div>
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.tone}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </Card>
            );
          })}
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-slate-950">Filtrar e organizar a leitura</h2>
              <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600">
                Use os filtros para separar o que vem do portal, do NR1, do questionário público ou dos serviços internos. A triagem continua sendo feita no mesmo lugar.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => refetch()} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </Button>
              <Button variant="ghost" onClick={clearFilters} className="gap-2">
                <Filter className="h-4 w-4" />
                Limpar filtros
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 xl:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(180px,0.45fr))]">
            <div className="flex gap-2">
              <Input
                placeholder="Buscar por mensagem, URL ou e-mail..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="text-sm"
              />
              <Button onClick={handleSearch} className="gap-1">
                <Search className="h-4 w-4" />
                Buscar
              </Button>
            </div>

            <Select value={originFilter} onValueChange={(value) => { setOriginFilter(value as OriginFilter); setPage(1); }}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas as origens</SelectItem>
                <SelectItem value="PLATAFORMA">Portal e administração</SelectItem>
                <SelectItem value="NR1">NR1</SelectItem>
                <SelectItem value="QUESTIONNAIRE">Questionário público</SelectItem>
                <SelectItem value="BACKEND">Serviços internos</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value as StatusFilter); setPage(1); }}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os status</SelectItem>
                <SelectItem value="NEW">Novos</SelectItem>
                <SelectItem value="REVIEWED">Em análise</SelectItem>
                <SelectItem value="RESOLVED">Resolvidos</SelectItem>
                <SelectItem value="IGNORED">Ignorados</SelectItem>
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={(value) => { setTypeFilter(value as TypeFilter); setPage(1); }}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Tipo técnico" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os tipos</SelectItem>
                <SelectItem value="FRONTEND">Frontend</SelectItem>
                <SelectItem value="BACKEND">Backend</SelectItem>
                <SelectItem value="QUESTIONNAIRE">Questionário</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="overflow-x-auto rounded-[1.5rem] border border-slate-200">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="border-b bg-slate-50">
                <tr>
                  <th className="w-16 px-4 py-3 text-left font-medium text-slate-500">#</th>
                  <th className="w-48 px-4 py-3 text-left font-medium text-slate-500">Origem</th>
                  <th className="w-36 px-4 py-3 text-left font-medium text-slate-500">Tipo</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500">Mensagem</th>
                  <th className="w-40 px-4 py-3 text-left font-medium text-slate-500">Contexto</th>
                  <th className="w-44 px-4 py-3 text-left font-medium text-slate-500">Data</th>
                  <th className="w-32 px-4 py-3 text-left font-medium text-slate-500">Status</th>
                  <th className="w-24 px-4 py-3 text-left font-medium text-slate-500">Ação</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="py-14 text-center text-slate-500">Carregando registros...</td>
                  </tr>
                ) : null}

                {!isLoading && items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-14 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                        <p>Nenhum log encontrado para esta combinação de filtros.</p>
                        <p className="text-xs">Você pode limpar os filtros ou atualizar a leitura.</p>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {!isLoading
                  ? items.map((log) => {
                      const statusInfo = STATUS_LABELS[log.status] || STATUS_LABELS.NEW;
                      const origin = getOriginFromLog(log);
                      const contextLabel = log.companyId
                        ? `Empresa #${log.companyId}`
                        : log.campaignId
                          ? `Campanha #${log.campaignId}`
                          : log.userEmail
                            ? log.userEmail
                            : "Sem detalhe";

                      return (
                        <tr
                          key={log.id}
                          className="cursor-pointer border-b last:border-0 transition-colors hover:bg-slate-50"
                          onClick={() => setSelectedId(log.id)}
                        >
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{log.id}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getOriginBadgeClasses(origin)}`}>
                              {ORIGIN_LABELS[origin]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                              {TYPE_LABELS[log.errorType] || log.errorType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="max-w-md truncate text-slate-900">{log.errorMessage}</p>
                            {log.url ? <p className="mt-1 truncate text-xs text-slate-500">{log.url}</p> : null}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{contextLabel}</td>
                          <td className="px-4 py-3 text-xs text-slate-600">{formatDate(log.createdAt)}</td>
                          <td className="px-4 py-3">
                            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedId(log.id);
                              }}
                            >
                              Ver
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  : null}
              </tbody>
            </table>
          </div>

          {data && data.total > 50 ? (
            <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-500">
                {data.total} registro(s) encontrados — página {page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                  Anterior
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
                  Próxima
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        <ErrorDetailModal errorId={selectedId} onClose={() => setSelectedId(null)} />
      </div>
    </SalviaLayout>
  );
}
